import { useSuspenseQuery } from '@tanstack/react-query';
import { DisciplineOptions, getDischargePlansOptions, type DischargePlansViewModel, type DisciplineDischargePlanViewModel } from '@generated';
import { Button } from '~/shared/components/buttons/Button';
import { Pen } from 'lucide-react';
import { SectionCard } from '~/shared/section/SectionCard';
import { DischargePlanFormModal } from './forms/DischargePlanFormModal';
import { NoDischargePlanFound } from './NoDischargePlanFound';
import { DischargePlan } from './DischargePlan';
import { Section } from '~/shared/section/Section';
import { SectionTitle } from '~/shared/section/SectionTitle';
import { DisciplineDischargePlan } from './DisciplineDischargePlan';

function getDisciplineDischargePlans(
	dischargePlans: DischargePlansViewModel
): { disciplineId: number; current?: DisciplineDischargePlanViewModel; pending: DisciplineDischargePlanViewModel }[] {
	return DisciplineOptions.reduce((acc, discipline) => {
		const current = dischargePlans.currentDisciplineDischargePlans.find(ddp => ddp.disciplineId === discipline.id);
		const pending = dischargePlans.pendingDisciplineDischargePlans.find(ddp => ddp.disciplineId === discipline.id);
		if (current || pending) {
			acc.push({ disciplineId: discipline.id, current, pending: pending! });
		}
		return acc;
	}, [] as { disciplineId: number; current?: DisciplineDischargePlanViewModel; pending: DisciplineDischargePlanViewModel }[]);
}

//pending only: show pending version with status Pending/InReview
//current & pending: show current + "Changes pending status"
//current only: current only no status
//none: no plan

export function CertificationPeriodDischargePlans({ certificationPeriodId }: { certificationPeriodId: string }) {
	const { data: dischargePlans } = useSuspenseQuery(getDischargePlansOptions(certificationPeriodId));
	const dischargePlan = dischargePlans.current ?? dischargePlans.pending;

	if (!dischargePlan) {
		return <NoDischargePlanFound certificationPeriodId={certificationPeriodId} />;
	}

	const disciplineDischargePlans = getDisciplineDischargePlans(dischargePlans);

	return (
		<Section
			header={
				<SectionTitle
					title="Current Discharge Plans"
					actions={
						<Button
							Icon={Pen}
							aria-label="Edit Discharge Plan"
							intent="neutral"
							renderModal={closeModal => (
								<DischargePlanFormModal certificationPeriodId={certificationPeriodId} closeModal={closeModal} />
							)}
						/>
					}
				/>
			}
		>
			<div className="grid grid-cols-1 gap-3">
				{!dischargePlan ? (
					<NoDischargePlanFound certificationPeriodId={certificationPeriodId} />
				) : (
					<DischargePlan dischargePlan={dischargePlan} estimatedDischargeDate={dischargePlans.lastVisitInCertPeriodDate} />
				)}

				<SectionCard title="Discipline Specific Goals">
					{disciplineDischargePlans.length > 0 ? (
						<div className="space-y-1.5">
							{disciplineDischargePlans.map(x => {
								return <DisciplineDischargePlan key={x.disciplineId} dischargePlan={x.pending} />;
							})}
						</div>
					) : (
						<span className="text-sm text-gray-700">No discipline specific goals found</span>
					)}
				</SectionCard>
			</div>
		</Section>
	);
}
